import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions,
} from '@tanstack/react-query';
import { campaignAPI } from '../campaignsAPI';
import type {
  CampaignsResult,
  CampaignFilterDef,
  CampaignQueryParams,
} from '../campaignsAPI';
import type {
  CampaignByIdQueryKey,
  CampaignByIdQueryOptions,
  CampaignByIdResponse,
} from '../types';
import apiClient from '@/shared/utils/axios';
import { screenAPI } from '../..';
import { isProd } from '@/shared/utils';
import { getCityGroupFilters } from '@/shared/utils/cityGroups';

export function useCampaignsQuery(
  params: CampaignQueryParams,
  options?: UseQueryOptions<CampaignsResult, unknown, CampaignsResult, any[]>
) {
  const {
    filters = {},
    search = '',
    page = 0,
    rowsPerPage = 10,
  } = params || {};

  const effectiveSearch = search.length > 2 ? search : '';
  const effectivePage = effectiveSearch ? 0 : page;

  return useQuery({
    queryKey: [
      'campaigns',
      { filters, search: effectiveSearch, page: effectivePage, rowsPerPage },
    ],
    queryFn: () =>
      campaignAPI.fetchCampaigns({
        filters,
        search: effectiveSearch,
        page: effectivePage,
        rowsPerPage,
      }),
    staleTime: 30_000,
    ...options,
  });
}

export function useCampaignFiltersQuery(
  options?: UseQueryOptions<
    CampaignFilterDef[],
    unknown,
    CampaignFilterDef[],
    any[]
  >
) {
  return useQuery({
    queryKey: ['campaignFilters'],
    queryFn: () => campaignAPI.fetchCampaignFilters(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

export function useCampaignByIdQuery(
  id: string | null,
  options?: CampaignByIdQueryOptions
) {
  return useQuery<
    CampaignByIdResponse,
    unknown,
    CampaignByIdResponse,
    CampaignByIdQueryKey
  >({
    queryKey: ['campaign', id],
    queryFn: () => campaignAPI.getCampaignById(id!),
    enabled: !!id, // Only run query if id exists
    staleTime: 30_000,
    ...options,
  });
}

// Advertiser Campaign Queries
export function useAdvertiserCampaignsQuery(
  params?: { page?: number; rowsPerPage?: number; search?: string },
  options?: UseQueryOptions<any, unknown, any, any[]>
) {
  return useQuery({
    queryKey: ['advertiserCampaigns', params],
    queryFn: () =>
      campaignAPI.fetchCampaigns({
        filters: {
          source: {
            key: 'source',
            type: 'terms',
            value: ['ADVERTISER'],
          },
        },
        page: params?.page ?? 0,
        rowsPerPage: params?.rowsPerPage ?? 10,
        search: params?.search,
      }),
    staleTime: 30_000,
    ...options,
  });
}

// Advertiser Campaign Screens Query
export function useScreensForTargetQuery(
  data: {
    state: string;
    city: string;
    cityGroupId?: string;
    partner_id?: string;
  } | null,
  options?: UseQueryOptions<any, unknown, any, any[]>
) {
  return useQuery({
    queryKey: ['screensForTarget', data],
    queryFn: async () => {
      if (!data) return { filters: [], screens: [] };

      let filters;

      // If cityGroupId is provided, use city group filters
      if (data.cityGroupId) {
        const cityGroupFilters = getCityGroupFilters(data.cityGroupId);
        if (cityGroupFilters) {
          filters = cityGroupFilters;
        } else {
          // Fallback to single state/city if city group not found
          filters = [
            {
              key: 'address.state',
              type: 'terms',
              value: [data.state],
            },
            {
              key: 'address.city',
              type: 'terms',
              value: [data.city],
            },
          ];
        }
      } else {
        // Use single state/city for backward compatibility
        filters = [
          {
            key: 'address.state',
            label: 'State',
            type: 'terms',
            value: [data.state],
          },
          {
            key: 'address.city',
            label: 'City',
            type: 'terms',
            value: [data.city],
          },
        ];
      }

      const payload = {
        filters,
        sort: [],
      };

      const deviceUrl = isProd
        ? '/dooh_api/screen/v1/devices/list?offset=0&limit=700'
        : '/dooh_api/screen/v1/advertiser/devices/list?offset=0&limit=60';

      const [filterRes, deviceRes] = await Promise.all([
        isProd
          ? screenAPI.fetchScreenFilters(payload.filters, 'advertiser')
          : campaignAPI.fetchAdvertiserFilters(payload.filters),
        apiClient.post<{ rows: Record<string, unknown>[] }>(deviceUrl, payload),
      ]);

      return {
        filters: filterRes?.success ? filterRes?.data : [],
        screens: deviceRes?.success ? deviceRes?.data?.rows : [],
      };
    },
    enabled: !!data && (!!data.cityGroupId || (!!data.state && !!data.city)),
    staleTime: 30_000,
    ...options,
  });
}

// Advertiser Campaign Mutations
export function useCreateCampaignMutation(
  options?: UseMutationOptions<any, unknown, any, any>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: (payload: any) => {
      return campaignAPI.createCampaign(payload);
    },

    onSuccess: async (response, variables, onMutateResult, context) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['advertiserCampaigns'] }),
        queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
      ]);
      await onSuccess?.(response, variables, onMutateResult, context);
    },
  });
}

export function useUpdateCampaignMutation(
  options?: UseMutationOptions<any, unknown, any, any>
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: ({
      campaignId,
      payload,
    }: {
      campaignId: string;
      payload: any;
    }) => campaignAPI.updateCampaign(campaignId, payload),
    onSuccess: async (response, variables, onMutateResult, context) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
        queryClient.invalidateQueries({ queryKey: ['advertiserCampaigns'] }),
        queryClient.invalidateQueries({ queryKey: ['screens'] }),
      ]);
      await onSuccess?.(response, variables, onMutateResult, context);
    },
  });
}

// Delete Campaign Mutation
export function useDeleteCampaignMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (campaignId: string) => campaignAPI.deleteCampaign(campaignId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useUpdateCampaignStatusMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      campaignId,
      status,
    }: {
      campaignId: string;
      status: string;
    }) => campaignAPI.updateCampaignStatus(campaignId, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['campaigns'] }),
  });
}

export function useTouchpointFiltersQuery(
  payload: { filters: any[]; sort: any[] } = { filters: [], sort: [] },
  options?: UseQueryOptions<any, unknown, any, any[]>
) {
  return useQuery({
    queryKey: ['touchpointFilters', payload],
    queryFn: () => campaignAPI.fetchTouchpointFilters(payload),
    staleTime: 30_000,
    ...options,
  });
}

export function useTouchpointsQuery(
  payload: { filters: any[]; sort: any[] } = { filters: [], sort: [] },
  options?: UseQueryOptions<any, unknown, any, any[]>
) {
  return useQuery({
    queryKey: ['touchpoints', payload],
    queryFn: () => campaignAPI.fetchTouchpoints(payload),
    staleTime: 0,
    ...options,
  });
}
